// consentLogic.ts — CORE-1404
//
// Чистые решения экрана согласия и синхронизации cookie: без React и сети,
// чтобы их можно было проверить тестами.

import type { AgeGroup } from '../screens/auth/ageChoiceLogic';
import {
  sameCookieChoice,
  serializeCookieChoice,
  type CookieChoice,
} from './cookieChoice';

export type GateSubmitPlan =
  | { kind: 'error'; messageKey: 'age.required' | 'legal.mustAccept' }
  | { kind: 'under14' }
  | { kind: 'save'; declareAge: 'adult' | 'teen' | null };

/**
 * CORE-1932 — подпись для экранного диктора и текст ошибки «галочка не
 * отмечена» в той же формулировке, что и первая галочка: с согласием на
 * обработку ПДн (российский контур) или прежняя — Условия и Политика.
 */
export function legalConsentKeys(withPersonalDataConsent: boolean): {
  a11y: 'legal.consentA11yPd' | 'legal.consentA11y';
  mustAccept: 'legal.mustAcceptPd' | 'legal.mustAccept';
  prefix: 'legal.pdPrefix' | 'legal.consentPrefix';
} {
  return withPersonalDataConsent
    ? { a11y: 'legal.consentA11yPd', mustAccept: 'legal.mustAcceptPd', prefix: 'legal.pdPrefix' }
    : { a11y: 'legal.consentA11y', mustAccept: 'legal.mustAccept', prefix: 'legal.consentPrefix' };
}

/** Документ, на который ведёт ссылка во фразе согласия. */
export type LegalPhraseDoc = 'terms-of-use' | 'personal-data-consent' | 'privacy-policy';

/** Кусок фразы согласия: простой текст или ссылка на документ. */
export interface LegalPhraseSegment {
  text: string;
  doc?: LegalPhraseDoc;
  /** Хвост testID ссылки: `legalTerms`, `legalPersonalData`, `legalPrivacy`. */
  linkId?: 'Terms' | 'PersonalData' | 'Privacy';
}

/**
 * Фраза согласия по кускам — то, что рисует `LegalLinks` (галочка регистрации
 * и экрана после соцвхода, строка на экране входа).
 *
 * CORE-1560 — с согласием на ПДн (российский контур) это текст юриста из
 * «Дисклеймеров», у которого заменено только начало (ответ 30.10 на вопрос 30):
 * «Отмечая это поле, я принимаю Условия использования, подтверждаю, что
 * ознакомился(-лась) с ними, и даю согласие на обработку персональных данных.
 * Подробнее об обработке данных читайте в Политике.» Ссылки — на Условия,
 * согласие на ПДн и Политику. Без него — прежняя фраза «… Условия использования
 * и Политику конфиденциальности».
 */
export function legalPhraseSegments(
  translate: (key: string) => string,
  prefix: string,
  withPersonalDataConsent: boolean,
): LegalPhraseSegment[] {
  const terms: LegalPhraseSegment = { text: translate('legal.terms'), doc: 'terms-of-use', linkId: 'Terms' };
  if (!withPersonalDataConsent) {
    return [
      { text: prefix },
      terms,
      { text: translate('legal.consentMid') },
      { text: translate('legal.privacy'), doc: 'privacy-policy', linkId: 'Privacy' },
    ];
  }
  return [
    { text: prefix },
    terms,
    { text: translate('legal.pdConsentMid') },
    { text: translate('legal.pdConsent'), doc: 'personal-data-consent', linkId: 'PersonalData' },
    { text: translate('legal.pdPolicyMid') },
    { text: translate('legal.pdPolicy'), doc: 'privacy-policy', linkId: 'Privacy' },
    { text: translate('legal.pdSuffix') },
  ];
}

/** CORE-1568 — почему сервер требует экран согласия (`consentReason`). */
export type ConsentGateReason = 'initial' | 'package_update';

export interface ConsentGateMode {
  titleKey: 'consentGate.title' | 'consentGate.updateTitle';
  bodyKey: 'consentGate.body' | 'consentGate.updateBody';
  /** Рекламная галочка — только при первом согласии: в пакет она не входит. */
  offerMarketing: boolean;
  /**
   * «Выйти» у нового аккаунта соцвхода без согласия удаляет его (CORE-1931). При
   * повторном принятии согласие уже есть — только выход, без попытки удаления.
   */
  deleteOnDecline: boolean;
}

/**
 * CORE-1568 — экран согласия по причине. Ответ юриста 30.10 (вопрос 19): кто
 * принимал документы прежнего оператора, принимает пакет целиком — Условия и
 * согласие на ПДн; до этого аккаунт ограничен тем же обязательным экраном.
 * Нет причины (старый сервер) — прежний экран CORE-1404.
 */
export function consentGateMode(reason: ConsentGateReason | null | undefined): ConsentGateMode {
  return reason === 'package_update'
    ? { titleKey: 'consentGate.updateTitle', bodyKey: 'consentGate.updateBody', offerMarketing: false, deleteOnDecline: false }
    : { titleKey: 'consentGate.title', bodyKey: 'consentGate.body', offerMarketing: true, deleteOnDecline: true };
}

/**
 * Что делать по нажатию «Продолжить» на экране согласия.
 *
 * Возраст спрашивается только у того, у кого его ещё нет на сервере; галочка —
 * всегда. «Младше 14» проверяется раньше галочки: такому человеку принимать
 * документы незачем, аккаунт удаляется.
 */
export function planGateSubmit(input: {
  serverAgeGroup: 'adult' | 'teen' | null | undefined;
  chosenAge: AgeGroup | null;
  legalAccepted: boolean;
}): GateSubmitPlan {
  const needsAge = !input.serverAgeGroup;
  if (needsAge) {
    if (input.chosenAge === null) return { kind: 'error', messageKey: 'age.required' };
    if (input.chosenAge === 'under14') return { kind: 'under14' };
  }
  if (!input.legalAccepted) return { kind: 'error', messageKey: 'legal.mustAccept' };
  return {
    kind: 'save',
    declareAge: needsAge ? (input.chosenAge as 'adult' | 'teen') : null,
  };
}

/**
 * CORE-1929 — предлагать ли рекламную галочку (регистрация, экран после
 * соцвхода). Только взрослому (CORE-1657: подросткам 14–17 не показываем, сервер
 * такое согласие не принимает) и только в российском контуре: решение владельца
 * 29.10 — вне РФ галочку не показываем, пока не решены юрлица (CORE-1559).
 * Контур — то же правило, что выбирает редакцию Условий (`isForeignContour`).
 */
export function marketingConsentOffered(
  ageGroup: AgeGroup | null | undefined,
  foreignContour: boolean,
): boolean {
  return ageGroup === 'adult' && !foreignContour;
}

/**
 * CORE-1657 — отправлять ли рекламное согласие. Только по отмеченной галочке и
 * только тому, кому галочку показали (`marketingConsentOffered`). Неотмеченная
 * галочка — это отсутствие записи, а не «отказ»: отправлять нечего.
 */
export function shouldSendMarketingConsent(
  marketingAccepted: boolean,
  ageGroup: AgeGroup | null | undefined,
  foreignContour: boolean,
): boolean {
  return marketingAccepted && marketingConsentOffered(ageGroup, foreignContour);
}

/**
 * CORE-1929 — где в строке галочки ссылка на полный текст согласия. Текст
 * галочки — дословно от юриста и записан в реестре редакций, поэтому ссылка
 * ставится на слова внутри него (`legal.marketingConsentLink`), а не
 * добавляется отдельной фразой: видимый текст и его редакция не меняются.
 * Если слов в строке не нашлось (перевод разошёлся), ссылка — вся строка.
 */
export function splitMarketingConsent(sentence: string, link: string): [string, string, string] {
  const at = link ? sentence.indexOf(link) : -1;
  if (at < 0) return ['', sentence, ''];
  return [sentence.slice(0, at), link, sentence.slice(at + link.length)];
}

export type CookieSyncAction =
  | { kind: 'none' }
  | { kind: 'push'; value: CookieChoice }
  | { kind: 'pull'; value: CookieChoice };

/**
 * Сверка выбора по cookie на устройстве и на сервере при входе.
 *
 * Выбор на устройстве свежее — его и отправляем: баннер показывается до входа,
 * и именно там человек отвечает. Если на устройстве ответа нет (новый браузер),
 * берём серверный, чтобы не спрашивать второй раз то, на что уже ответили.
 */
export function cookieSyncOnLogin(
  local: CookieChoice | null | undefined,
  server: CookieChoice | null | undefined,
): CookieSyncAction {
  if (local) return sameCookieChoice(local, server) ? { kind: 'none' } : { kind: 'push', value: local };
  if (server) return { kind: 'pull', value: server };
  return { kind: 'none' };
}

/**
 * CORE-1659 — что именно уходит на сервер. Ответ прежнему баннеру («принять» /
 * «отклонить» на всё) записывается под прежней версией текста и прежним
 * значением: человек отвечал на него, а выбора по категориям ещё не видел.
 */
export function cookieConsentRecord(
  choice: CookieChoice,
  legacy: boolean,
  versions: { current: string; legacy: string },
): { value: string; documentVersion: string } {
  if (legacy && choice.analytics === choice.marketing) {
    return { value: choice.analytics ? 'accepted' : 'declined', documentVersion: versions.legacy };
  }
  return { value: serializeCookieChoice(choice), documentVersion: versions.current };
}

/**
 * 14–17 лет: сторонние счётчики не грузятся независимо от выбора по cookie
 * (до решения юриста). Для гостя и взрослого решает выбор.
 *
 * CORE-1775 — `blocked`, а не «только необходимые»: Метрика теперь в
 * необходимых, и «только необходимые» её бы загрузили.
 */
export function effectiveCookieChoice(
  choice: CookieChoice | null | undefined,
  ageGroup: 'adult' | 'teen' | null | undefined,
): CookieChoice | 'unknown' | 'blocked' {
  if (ageGroup === 'teen') return 'blocked';
  return choice ?? 'unknown';
}

/**
 * CORE-1930 — видна ли строка «Рекламные сообщения» в настройках (веб и
 * приложение). Правило то же, что у галочки (`marketingConsentOffered`), с
 * двумя оговорками:
 * - действующее согласие отзываемо всегда — строка видна, пока оно есть, даже
 *   вне российского контура (CORE-1929);
 * - возраст `null` — аккаунт до CORE-1404, подростком он не назван; строка у
 *   такого была и до CORE-1930, её не прячем. Подросткам (14–17) — никогда.
 */
export function marketingSettingVisible(
  ageGroup: 'adult' | 'teen' | null | undefined,
  granted: boolean,
  foreignContour: boolean,
): boolean {
  if (ageGroup === 'teen') return false;
  return granted || marketingConsentOffered(ageGroup ?? 'adult', foreignContour);
}

export type MarketingSettingError = 'rejected' | 'failed';

/**
 * CORE-1930 — что сказать, если сервер не сохранил выбор. 400 — сервер
 * согласие не принимает (например, INVALID_CONSENT вне российского контура):
 * повтор не поможет. Остальное — сбой, который стоит повторить.
 */
export function marketingSettingErrorKind(err: unknown): MarketingSettingError {
  const status = (err as { status?: number } | null | undefined)?.status;
  return status === 400 ? 'rejected' : 'failed';
}
